import Vue from 'vue';
import Vuex from 'vuex';
import router from '../router';
import createPersistedState from 'vuex-persistedstate';
import { STATUS, RESPONSE_STATUSES, ERROR_MESSAGE } from '../components/shared/constants';
import { version } from '../../package.json';

Vue.use(Vuex);
export const USERMANAGEMENT_SERVICE = process.env.VUE_APP_SERVICES_BASE_URL;
export const ACCOUNT_ID = process.env.VUE_APP_ACCOUNT_ID;
export const TOTAL_NUMBER_OF_REQUESTED_USERS = process.env.VUE_APP_TOTAL_USERS_REQUESTED;
export const PAGINATION_PAGE_SIZE = process.env.VUE_APP_PAGINATION_PAGE_SIZE;

const state = {
    account: {},
    users: {},
    userStatus: STATUS.ACTIVE,
    userSearch: {
        firstName: "",
        lastName: "",
        email: ""
    },
    seatCount: 0,
    activeUsers: 0,
    disabledUsers: 0,
    adminInfo : {
        adminFirstName: "",
        adminLastName: "",
    },
    pageNumber: 0,
    totalNumberOfRequestedUsers: TOTAL_NUMBER_OF_REQUESTED_USERS,
    paginationPageSize: PAGINATION_PAGE_SIZE,
    selectedUsers: [],
    accountIds: [ACCOUNT_ID], // Hard coded in client temporarily until replaced by information retrieved from getAccounts endpoint
    accountIdIndex: 0,
    authToken: localStorage.getItem('user-token') || "",
    sortBy: "",
    sortOrder: "",
    modal: {
        isVisible: false,
        type: '',
        message: 'You have set 1 user to disabled',
        title: 'Success',
        isFooter: false
    },
    error: false,
    errorTitle: ERROR_MESSAGE.DEFAULT.title,
    errorBody: ERROR_MESSAGE.DEFAULT.body,
    loggedIn: false,
    supportTag: {
        clientVersion: ''
    }
};

const getters = {
    account: state => state.account,
    users: state => state.users.users,
    userStatus: state => state.userStatus,
    seatCount: state => state.seatCount,
    activeUsers: state => state.activeUsers,
    disabledUsers: state => state.disabledUsers,
    firstNameSearch: state => state.userSearch.firstName,
    lastNameSearch: state => state.userSearch.lastName,
    emailSearch: state => state.userSearch.email,
    pageNumber: state => state.pageNumber,
    adminFirstName: state => state.adminInfo.adminFirstName,
    adminLastName: state => state.adminInfo.adminLastName,
    selectedUsers: state => state.selectedUsers,
    selectedUserIds: state => {
        return state.selectedUsers.map(user => {
            return user.utdId
        });
    },
    selectedActiveUsers: state => {
        return state.selectedUsers.reduce((selectedActive, node) => {
            return selectedActive || node.status === STATUS.ACTIVE;
        }, false);
    },
    selectedDisabledUsers: state => {
        return state.selectedUsers.reduce((selectedActive, node) => {
            return selectedActive || node.status === STATUS.DISABLED;
        }, false);
    },
    accountIds: state => state.accountIds,
    paginationPageSize: state => state.paginationPageSize,
    authToken: state => state.authToken,
    isAuthenticated: state => !!state.authToken,
    sortBy: state => state.sortBy,
    sortOrder: state => state.sortOrder,
    isModalVisible: state => state.modal.isVisible,
    modalType: state =>  state.modal.type,
    modalMessage: state =>  state.modal.message,
    modalTitle: state =>  state.modal.title,
    modalFooter: state =>  state.modal.isFooter,
    error: state => state.error,
    errorTitle: state => state.errorTitle,
    errorBody: state => state.errorBody,
    loggedIn: state => state.loggedIn,
    accountId: state => {
        if (state.accountIdIndex >= state.accountIds.length) return null;
        
        return state.accountIds[state.accountIdIndex];
    },
    supportTag: state => state.supportTag,

    getUsersUrl: (state, getters) => `${USERMANAGEMENT_SERVICE}accounts/${getters.accountId}` 
        + `/users/json?page=${state.pageNumber}&page_size=${state.totalNumberOfRequestedUsers}`
        + `&user_status=${STATUS.ACTIVE}`,
    getFilteredUsersUrl: (state, getters) => `${USERMANAGEMENT_SERVICE}accounts/${getters.accountId}`
    + `/users/search/json?page=${state.pageNumber}&page_size=${state.totalNumberOfRequestedUsers}` 
    + `&sort_by=${state.sortBy}&sort_order=${state.sortOrder}`
    + `&first_name=${state.userSearch.firstName}&last_name=${state.userSearch.lastName}`
    + `&email=${state.userSearch.email}`
    +`&user_status=${state.userStatus}`, 
    getAccount: (state, getters) => `${USERMANAGEMENT_SERVICE}accounts/${getters.accountId}`
        + `/json`,        
    updateUsersUrl: (state, getters) => `${USERMANAGEMENT_SERVICE}accounts/${getters.accountId}`
        + `/users/status/json`,
    getClientVersionUrl: () => `/management/info`
};

export const SET_ACCOUNT = 'SET_ACCOUNT';
export const SET_USERS = 'SET_USERS';
export const SET_USER_STATUS = 'SET_USER_STATUS';
export const SET_AUTH_TOKEN = 'SET_AUTH_TOKEN';
export const SET_SELECTED_USERS = 'SET_SELECTED_USERS';
export const SET_ADMIN_FIRST_NAME = 'SET_ADMIN_FIRST_NAME';
export const SET_ADMIN_LAST_NAME = 'SET_ADMIN_LAST_NAME';
export const SET_MODAL_MESSAGE = 'SET_MODAL_MESSAGE';
export const SET_MODAL_TITLE = 'SET_MODAL_TITLE';
export const SET_MODAL_VISIBILITY = 'SET_MODAL_VISIBILITY';
export const SET_MODAL_TYPE = 'SET_MODAL_TYPE';
export const SET_MODAL_FOOTER_VISIBILITY = 'SET_MODAL_FOOTER_VISIBILITY';
export const SET_ERROR_STATE = 'SET_ERROR_STATE';
export const SET_ERROR_TITLE = 'SET_ERROR_TITLE';
export const SET_ERROR_BODY = 'SET_ERROR_BODY';
export const SET_FULLY_LOGGED_IN = 'SET_FULLY_LOGGED_IN';
export const SET_FIRSTNAME_SEARCH = "SET_FIRSTNAME_SEARCH";
export const SET_LASTNAME_SEARCH = "SET_LASTNAME_SEARCH";
export const SET_EMAIL_SEARCH = "SET_EMAIL_SEARCH";
export const SET_SORT_BY = 'SET_SORT_BY';
export const SET_SORT_ORDER = 'SET_SORT_ORDER';
export const SET_SUPPORT_TAG_CLIENT = 'SET_SUPPORT_TAG_CLIENT';

export const mutations = {
    [SET_ACCOUNT]: (state, account) => {
        state.account = account;
        state.seatCount = account.seatCount;
        state.activeUsers = account.userStatsInfo.activeUsersCount;
        state.disabledUsers = account.userStatsInfo.disabledUserCount;
    },
    [SET_USERS]: (state, users) => state.users = users,
    [SET_USER_STATUS]: (state, userStatus) => state.userStatus = userStatus,
    [SET_AUTH_TOKEN]: (state, authToken) => {
        state.authToken = authToken;
        window.localStorage.setItem('token', authToken);
    },
    [SET_ADMIN_FIRST_NAME]: (state, adminFirstName) => state.adminInfo.adminFirstName = adminFirstName,
    [SET_ADMIN_LAST_NAME]: (state, adminLastName) => state.adminInfo.adminLastName = adminLastName,
    [SET_SELECTED_USERS]: (state, selectedUsers) => state.selectedUsers = selectedUsers,
    [SET_MODAL_MESSAGE]: (state, message) => state.modal.message = message,
    [SET_MODAL_TITLE]: (state, title) => state.modal.title = title,
    [SET_MODAL_VISIBILITY]: (state, visible) => state.modal.isVisible = visible,
    [SET_MODAL_TYPE]: (state, type) => state.modal.type = type,
    [SET_MODAL_FOOTER_VISIBILITY]: (state, visible) => state.modal.isFooter = visible,
    [SET_ERROR_STATE]: (state, error) => state.error = error,
    [SET_ERROR_TITLE]: (state, errorTitle) => state.errorTitle = errorTitle,
    [SET_ERROR_BODY]: (state, errorBody) => state.errorBody = errorBody,
    [SET_FULLY_LOGGED_IN]: (state, loggedIn) => state.loggedIn = loggedIn,
    [SET_FIRSTNAME_SEARCH]: (state, firstNameSearch) => state.userSearch.firstName = firstNameSearch,
    [SET_LASTNAME_SEARCH]: (state, lastNameSearch) => state.userSearch.lastName = lastNameSearch,
    [SET_EMAIL_SEARCH]: (state, emailSearch) => state.userSearch.email = emailSearch,
    [SET_SORT_BY]: (state, sortBy) => state.sortBy = sortBy,
    [SET_SORT_ORDER]: (state, sortOrder) => state.sortOrder = sortOrder,
    [SET_SUPPORT_TAG_CLIENT]: (state, clientVersion) => state.supportTag.clientVersion = clientVersion
};

export const actions = {
    async getAccount({ commit, getters }) {
        return new Promise((resolve, reject) => {
            const xhttp = new XMLHttpRequest();
            xhttp.open("GET", getters.getAccount, true);
            xhttp.setRequestHeader("Authorization", `Bearer ${getters.authToken}`);
            xhttp.send();

            xhttp.onreadystatechange = () => {
                if(xhttp.readyState === XMLHttpRequest.DONE) {
                    if (xhttp.status === RESPONSE_STATUSES.SUCCESS && xhttp.responseText !== undefined) {
                        commit(SET_ACCOUNT, JSON.parse(xhttp.responseText));
                        resolve(xhttp.response);
                    }
                    else {
                        reject(xhttp.status);
                    }
                }
            };

            xhttp.onerror = () => reject(xhttp.status);

        });
    },

    async getUsers({ commit, getters }) {
        return new Promise((resolve, reject) => {
            if (getters.accountId === null) {
                console.error("Cannot find account");
                reject();
                return;
            }

            const xhttp = new XMLHttpRequest();
            xhttp.open("GET", getters.getUsersUrl, true);
            xhttp.setRequestHeader("Authorization", `Bearer ${getters.authToken}`);
            xhttp.send();

            xhttp.onreadystatechange = () => {
                if(xhttp.readyState === XMLHttpRequest.DONE) {
                    if (xhttp.status === RESPONSE_STATUSES.SUCCESS && xhttp.responseText !== undefined) {
                        commit(SET_USERS, JSON.parse(xhttp.responseText));
                        resetState(commit);
                        resolve(xhttp.response);
                    }

                    else {
                        reject(xhttp.status);
                    }
                }
            };

            xhttp.onerror = () => reject(xhttp.status);

        });
    },
    async getFilteredUsers({ commit, getters }) {
        return new Promise((resolve, reject) => {
            if (getters.accountId === null) {
                console.error("Cannot find account");
                reject();
                return;
            }

            const xhttp = new XMLHttpRequest();
            xhttp.open("GET", getters.getFilteredUsersUrl, true);
            xhttp.setRequestHeader("Authorization", `Bearer ${getters.authToken}`);
            xhttp.send();

            xhttp.onreadystatechange = () => {
                if(xhttp.readyState === XMLHttpRequest.DONE) {
                    if (xhttp.status === RESPONSE_STATUSES.SUCCESS && xhttp.responseText !== undefined) {
                        commit(SET_USERS, JSON.parse(xhttp.responseText));
                        commit(SET_SELECTED_USERS, []);
                        resolve(xhttp.response);
                    }

                    else {
                        reject(xhttp.status);
                    }
                }
            };

            xhttp.onerror = () => reject(xhttp.status);
        });
    },

    async updateUsers({ getters }, data) {
        return new Promise((resolve, reject) => {
            if (getters.accountId === null) {
                console.error("Cannot find account");
                reject();
                return;
            }

            const params = {
                "userStatus": data.status,
                "utdIds": getters.selectedUserIds
            };
            const xhttp = new XMLHttpRequest();
            xhttp.open("POST", getters.updateUsersUrl, true);
            xhttp.setRequestHeader("Authorization", `Bearer ${getters.authToken}`);
            xhttp.setRequestHeader("Content-Type", "application/json");
            xhttp.send(JSON.stringify(params));

            xhttp.onreadystatechange = () => {
                if(xhttp.readyState == XMLHttpRequest.DONE) {
                    if (xhttp.status === RESPONSE_STATUSES.SUCCESS) {
                        resolve(xhttp.response);
                    }

                    else {
                        reject(xhttp.status);
                    }
                }
            };

            xhttp.onerror = () => reject(xhttp.status);

        });
    },
    async loginSuccess({ commit, dispatch, getters }, data) {
        commit(SET_AUTH_TOKEN, data.token);
        if(data.givenName) {
            commit(SET_ADMIN_FIRST_NAME, data.givenName);
        }    
        if(data.familyName) {
            commit(SET_ADMIN_LAST_NAME, data.familyName);
        }
       
        try {
            await dispatch('getUsers').then(() => {
                commit(SET_ERROR_STATE, false);
                localStorage.setItem('token', getters.authToken);
                router.push("/manage");
            });
        } catch (e) {
            commit(SET_ERROR_STATE, true);
            if (e === RESPONSE_STATUSES.AUTHORIZATION_FAILED) {
                commit(SET_ERROR_TITLE, ERROR_MESSAGE.AUTHORIZATION_FAILED.title);
                commit(SET_ERROR_BODY, ERROR_MESSAGE.AUTHORIZATION_FAILED.body);
            }
            else if (e === RESPONSE_STATUSES.INTERNAL_SERVER) {
                commit(SET_ERROR_TITLE, ERROR_MESSAGE.INTERNAL_SERVER.title);
                commit(SET_ERROR_BODY, ERROR_MESSAGE.INTERNAL_SERVER.body);
            }
            else {
                commit(SET_ERROR_TITLE,ERROR_MESSAGE.DEFAULT.title);
                commit(SET_ERROR_BODY, ERROR_MESSAGE.DEFAULT.body);
            }
            commit(SET_AUTH_TOKEN, '');
            localStorage.removeItem('token');
            router.push("/error");
        }
    },
    showModal({ commit }, data){
        commit(SET_MODAL_VISIBILITY, true);
        commit(SET_MODAL_MESSAGE, data.message);
        commit(SET_MODAL_TITLE, data.title);
        commit(SET_MODAL_TYPE, data.type);
        commit(SET_MODAL_FOOTER_VISIBILITY, data.isFooter);
    },
    hideModal({ commit }) {
        commit(SET_MODAL_VISIBILITY, false);
        commit(SET_MODAL_MESSAGE, '');
        commit(SET_MODAL_TITLE, '');
        commit(SET_MODAL_TYPE, '');
        commit(SET_MODAL_FOOTER_VISIBILITY, false);
    },

    logoutUser({commit}){
        commit(SET_AUTH_TOKEN,'');
        commit(SET_FULLY_LOGGED_IN, false);
        localStorage.removeItem("vuex");
        if (router.currentRoute.path !== "/login") {
            window.location.assign('/login');
        }
    },
    
    async getClientVersion({ commit, getters }) {
        return new Promise((resolve, reject) => {
            const xhttp = new XMLHttpRequest();
            xhttp.open("GET", getters.getClientVersionUrl, true);
            xhttp.send();

            xhttp.onreadystatechange = () => {
                if(xhttp.readyState === XMLHttpRequest.DONE) {
                    if (xhttp.status === RESPONSE_STATUSES.SUCCESS && xhttp.responseText !== undefined) {
                        commit(SET_SUPPORT_TAG_CLIENT, JSON.parse(xhttp.responseText).version);
                        resolve(xhttp.response);
                    }
                    else {
                        commit(SET_SUPPORT_TAG_CLIENT, version);
                    }
                }
            };

            xhttp.onerror = () => reject(xhttp.status);

        });
    },
};

const resetState = (commit) => {
    commit(SET_SORT_BY, '');
    commit(SET_SORT_ORDER, '');
    commit(SET_USER_STATUS, STATUS.ACTIVE);
    commit(SET_FIRSTNAME_SEARCH, '');
    commit(SET_LASTNAME_SEARCH, '');
    commit(SET_EMAIL_SEARCH, '');
    commit(SET_SELECTED_USERS, []);
}

export default new Vuex.Store({
    state,
    getters,
    mutations,
    actions,
    plugins: [createPersistedState()]
});